<template>
    <div v-if="getId" class="signature-container" :class="{ 'signature-view-mode': viewSubmissionMode }">
        <!-- Add a label to help users understand what to do -->
        <div class="signature-instructions" v-if="!viewSubmissionMode && !builderMode">
            Sign here using your finger or mouse
        </div>

        <div class="signature-wrapper" :class="{ 'is-loading': isLoading }">
            <!-- Loading overlay -->
            <div v-if="isLoading" class="signature-loading-overlay">
                <span>Loading signature...</span>
            </div>

            <!-- Error message -->
            <div v-if="loadError" class="signature-error">
                {{ loadError }}
            </div>

            <VPerfectSignature
                class="signature-pad"
                :stroke-options="strokeOptions"
                :pen-color="penColor"
                ref="signaturePad"
                @onBegin="onBegin"
                @onEnd="onEnd"
                :willReadFrequently="true"/>
        </div>

        <div v-if="!viewSubmissionMode && !builderMode" id="clear-signature" @click="clear">
            <span>Clear signature</span>
        </div>
    </div>
</template>

<script>
    import BaseControlSkeleton from "../../skeletons/controls/BaseControlSkeleton";
    import pako from 'pako'
    import VPerfectSignature from 'v-perfect-signature';
    import fitStrokesToCanvas from "../../libraries/fit-strokes-to-canvas";

    // Create a debounce utility function
    function debounce(func, wait) {
        let timeout;
        return function(...args) {
            clearTimeout(timeout);
            timeout = setTimeout(() => func.apply(this, args), wait);
        };
    }

    export default {
        name: "SignatureControl",
        extends: BaseControlSkeleton,
        props: ['viewSubmissionMode'],
        components: {VPerfectSignature},

        data: () => ({
            strokeOptions: {
                size: 8,                // Reduced for better performance
                thinning: 0.5,          // Optimized values
                smoothing: 0.3,         // Reduced for better performance
                streamline: 0.3,        // Reduced for better performance  
                stepSize: 10,           // Further reduced points
                last: true              // Optimize for last-point tracking
            },
            penColor: '#000000',        // Default pen color
            resizeTimeout: null,
            endTimeout: null,
            isProcessing: false,
            isIOS: false,               // Will be set in mounted hook
            isLoading: false,           // Track loading state
            loadError: null,            // Track loading errors
            isMobile: false,            // Will be set in mounted hook
            lastSignatureData: null,    // Store the last signature data to prevent loss on blur
        }),

        watch: {
            value: {
                immediate: true,
                async handler(val) {
                    if (!this.$refs.signaturePad) return;

                    // Clear the pad if value is empty/null/undefined
                    if (!val || val.length === 0 || Array.isArray(val) && val.length === 0) {
                        this.$refs.signaturePad.clear();
                        this.lastSignatureData = null;
                        return;
                    }

                    this.isLoading = true;
                    this.loadError = null;

                    try {
                        const decompressedData = JSON.parse(pako.ungzip(val, { to: 'string' }));
                        await this.$nextTick();
                        this.$refs.signaturePad.fromData(this.displayStrokes(decompressedData));
                        this.lastSignatureData = val;
                    } catch (error) {
                        console.error('Error loading signature data:', error);
                        this.loadError = 'Failed to load signature data';
                        this.$refs.signaturePad?.clear();
                        this.lastSignatureData = null;
                    } finally {
                        this.isLoading = false;
                    }
                }
            }
        },

        mounted() {
            // Detect device types
            this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            this.isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);

            this.$nextTick(() => {
                if (this.$refs.signaturePad) {
                    // Initialize with proper sizing
                    this.$refs.signaturePad.resizeCanvas(false);
                    
                    // Add only essential event listeners with debouncing
                    window.addEventListener('resize', this.handleDebounceResize);
                    
                    // Visibility change is important for tab switching
                    document.addEventListener('visibilitychange', this.handleVisibilityChange);
                    
                    // For mobile orientation changes
                    if (this.isMobile) {
                        window.addEventListener('orientationchange', this.handleDebounceResize);
                        
                        // Prevent elastic scrolling on iOS
                        if (this.isIOS) {
                            this.strokeOptions.size = 10; // Thicker line on iOS
                            document.body.addEventListener('touchmove', this.preventElasticScrolling, { passive: false });
                        }
                    }

                    // Load initial signature data if available
                    if (this.value) {
                        try {
                            const decompressedData = JSON.parse(pako.ungzip(this.value, { to: 'string' }));
                            this.$refs.signaturePad.fromData(this.displayStrokes(decompressedData));
                        } catch (error) {
                            console.error('Error loading initial signature data:', error);
                        }
                    }
                }
            });
        },

        beforeDestroy() {
            // Remove event listeners
            window.removeEventListener('resize', this.handleDebounceResize);
            document.removeEventListener('visibilitychange', this.handleVisibilityChange);
            
            if (this.isMobile) {
                window.removeEventListener('orientationchange', this.handleDebounceResize);
                
                if (this.isIOS) {
                    document.body.removeEventListener('touchmove', this.preventElasticScrolling);
                }
            }

            // Clear any pending timeouts
            if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
            if (this.endTimeout) clearTimeout(this.endTimeout);
        },

        methods: {
            onBegin() {
                // Signature started - no action needed
            },

            onEnd() {
                // Prevent multiple simultaneous processing
                if (this.isProcessing) return;

                // Clear any existing timeout
                if (this.endTimeout) clearTimeout(this.endTimeout);

                // Set a timeout to debounce processing
                this.endTimeout = setTimeout(() => {
                    this.isProcessing = true;

                    try {
                        // Get original signature data
                        const originalData = this.$refs.signaturePad.toData();

                        // Skip processing if no data
                        if (!originalData || originalData.length === 0) {
                            this.updateValue(null);
                            this.isProcessing = false;
                            return;
                        }

                        // Use setTimeout to prevent UI blocking
                        setTimeout(() => {
                            try {
                                // Compress the data efficiently
                                const reducedData = pako.gzip(JSON.stringify(originalData));
                                
                                // Store the signature data
                                this.lastSignatureData = reducedData;
                                this.updateValue(reducedData);
                            } catch (error) {
                                console.error('Error compressing signature:', error);
                                this.updateValue(null);
                            } finally {
                                this.isProcessing = false;
                            }
                        }, 0);
                    } catch (error) {
                        console.error('Error processing signature:', error);
                        this.updateValue(null);
                        this.isProcessing = false;
                    }
                }, 300); // 300ms debounce
            },

            // More efficient debounced resize handler
            handleDebounceResize: debounce(function() {
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.resizeCanvas(false);
                    
                    // If we have signature data, restore it after resize
                    if (this.lastSignatureData) {
                        try {
                            const decompressedData = JSON.parse(pako.ungzip(this.lastSignatureData, { to: 'string' }));
                            this.$refs.signaturePad.fromData(this.displayStrokes(decompressedData));
                        } catch (error) {
                            console.error('Error restoring signature after resize:', error);
                        }
                    }
                }
            }, 250),

            handleVisibilityChange() {
                // When tab becomes visible again, restore signature if needed
                if (document.visibilityState === 'visible' && this.lastSignatureData && this.$refs.signaturePad) {
                    // Use a single attempt with short delay
                    setTimeout(() => {
                        try {
                            this.$refs.signaturePad.resizeCanvas(false);
                            const decompressedData = JSON.parse(pako.ungzip(this.lastSignatureData, { to: 'string' }));
                            this.$refs.signaturePad.fromData(this.displayStrokes(decompressedData));
                        } catch (error) {
                            console.error('Error restoring signature on visibility change:', error);
                        }
                    }, 150);
                }
            },

            preventElasticScrolling(e) {
                // Only prevent default if the touch is on the signature pad
                const target = e.target;
                const signaturePadEl = this.$refs.signaturePad?.$el;
                if (signaturePadEl && (target === signaturePadEl || signaturePadEl.contains(target))) {
                    e.preventDefault();
                }
            },

            clear() {
                if (this.$refs.signaturePad) {
                    this.$refs.signaturePad.clear();
                    this.lastSignatureData = null;
                    this.updateValue(null);
                }
            },

            /**
             * In the read-only submission view / PDF, fit the whole signature into
             * the (often smaller) render canvas so it isn't clipped by the
             * overflow:hidden wrapper. In fill/edit mode we replay the strokes as-is,
             * since the canvas matches where they were drawn. Display-only — the
             * stored data is never modified. (Fixes signature cut-off, 2026-07-01.)
             */
            displayStrokes(strokes) {
                if (!this.viewSubmissionMode) return strokes;
                const canvas = this.$el && this.$el.querySelector('canvas');
                if (!canvas) return strokes;
                return fitStrokesToCanvas(strokes, canvas.clientWidth, canvas.clientHeight);
            }
        },

        computed: {
            builderMode() {
                if (this.$parent.readOnly || this.$parent.readOnly === false)
                    return false;
                else
                    return true;
            },

            getId() {
                return `signature-pad-${this.control.uniqueId}`;
            }
        }
    }
</script>

<style scoped>
.signature-container {
  margin-bottom: 15px;
}

.signature-instructions {
  margin-bottom: 8px;
  font-size: 14px;
  color: #555;
  text-align: center;
}

.signature-wrapper {
  position: relative;
  border: 3px rgba(0, 0, 0, .4) dotted;
  border-radius: 4px;
  background-color: rgba(255, 255, 255, 0.9);
  overflow: hidden; /* Prevent overflow issues */
  will-change: transform; /* Performance optimization */
  z-index: 1; /* Ensure proper stacking context */
}

.signature-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.9);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 2;
}

.signature-error {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    color: #dc3545;
    background: rgba(255, 255, 255, 0.9);
    padding: 8px 16px;
    border-radius: 4px;
    z-index: 2;
}

.signature-wrapper.is-loading {
    opacity: 0.7;
    pointer-events: none;
}

.signature-pad {
  position: relative;
  cursor: crosshair;
  height: 200px;
  width: 100%;
  max-width: 100%;
  touch-action: none; /* Prevent scrolling while signing */
  -webkit-user-select: none; /* Prevent selection on iOS */
  user-select: none;
  z-index: 1;
  /* Performance optimizations */
  will-change: transform;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

#clear-signature {
  position: relative;
  margin-top: 8px;
  padding: 5px 10px;
  background-color: #f8f8f8;
  border: 1px solid #ddd;
  border-radius: 4px;
  display: inline-block;
  cursor: pointer;
  font-size: 14px;
  color: #555;
  text-align: center;
  width: 100%;
}

#clear-signature:hover {
  background-color: #f0f0f0;
}

#clear-signature:active {
  background-color: #e8e8e8;
}

/* View/PDF mode only: give the signature a taller box so it's legible instead of
   squished into the ~94px the library's inline height collapses it to. Edit/sign
   mode (no .signature-view-mode class) is unchanged. The fit helper (displayStrokes)
   scales the signature to whatever size this makes the canvas, so it stays whole. */
.signature-view-mode .signature-pad {
  height: 180px !important;
  width: 100% !important;
  display: block !important;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  .signature-instructions {
    font-size: 16px;
  }

  .signature-wrapper {
    border-width: 2px;
  }

  .signature-pad {
    height: 180px;
  }

  #clear-signature {
    padding: 10px 15px;
    font-size: 16px;
    margin-top: 10px;
  }
}

/* iOS specific styles */
@supports (-webkit-touch-callout: none) {
  .signature-wrapper {
    border-width: 2px;
    -webkit-transform: translate3d(0, 0, 0);
  }

  .signature-pad {
    height: 180px !important;
    -webkit-transform: translate3d(0, 0, 0) !important;
  }
}
</style>